import { useEffect, useMemo, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import SiteEnergySource from "../../../../../../shared/components/site-energy-source/site-energy-source";
import EnergyFlowImg from "../../../../../../shared/oversight-core/assets/images/vrm-energy-flow-center.svg";
import { IInstance } from "../../../../../../shared/oversight-core/interfaces/instance";
import { IVrmSolarPlantView } from "../../../../../../shared/oversight-core/interfaces/vrm-solar-plant-view";
import Helper from "../../../../../../shared/oversight-core/utils/helpers";
import EnergyFlowNode from "../components/energy-flow-node/energy-flow-node";
import EnergyFlowPath from "../components/energy-flow-path/energy-flow-path";
import styles from "./energy-flow.module.scss";

interface IProps {
  solarPlant: IVrmSolarPlantView;
}

interface IAttributes {
  attributeCode?: string;
  value1?: string;
  value2?: string;
  value3?: string;
}

const chartData: { value: number }[] = [
  { value: 100 },
  { value: 130 },
  { value: 210 },
  { value: 190 },
  { value: 240 },
  { value: 300 },
  { value: 220 },
  { value: 240 },
  { value: 190 },
  { value: 230 },
];

const VrmEnergyFlow = (props: IProps) => {
  const { solarPlant } = props;

  const [gridAttributes, setGridAttributes] = useState<IAttributes[]>();
  const [dcPowerAttributes, setDcPowerAttributes] = useState<IAttributes[]>();
  const [batteryAttributes, setBatteryAttributes] = useState<IAttributes[]>();
  const [pvInverterAttributes, setPvInverterAttributes] =
    useState<IAttributes[]>();
  const [loadAttributes, setLoadAttributes] = useState<IAttributes[]>();
  const [pvChargerAttributes, setPvChargerAttributes] =
    useState<IAttributes[]>();

  function ensureArray<T>(input: unknown): T[] {
    return Array.isArray(input) ? input : [];
  }

  const getGridValue = useMemo(() => {
    return solarPlant?.from_to_grid?.formattedValue;
  }, [solarPlant]);

  const getGeneratorValue = useMemo(() => {
    return solarPlant?.generator?.formattedValue;
  }, [solarPlant]);

  const getDCPowerValue = useMemo(() => {
    return solarPlant?.dc?.formattedValue;
  }, [solarPlant]);

  const getBatteryValue = useMemo(() => {
    return solarPlant?.bs?.formattedValue;
  }, [solarPlant]);

  const getBatteryPowerValue = useMemo(() => {
    return solarPlant?.bp?.formattedValue;
  }, [solarPlant]);

  const getPVChargerValue = useMemo(() => {
    const values = solarPlant?.PVP?.instances
      ? Object.values(solarPlant.PVP.instances)
      : [];

    return Number(
      Helper.roundTo2(values.reduce((sum, val) => sum + (val.rawValue || 0), 0))
    );
  }, [solarPlant]);

  const getPVInverterValue = useMemo(() => {
    return solarPlant?.pv_inverter?.formattedValue;
  }, [solarPlant]);

  const getLoadValue = useMemo(() => {
    return solarPlant?.ac_loads?.formattedValue;
  }, [solarPlant]);

  const getBatteryStatus = useMemo(() => {
    return solarPlant?.bst?.rawValue;
  }, [solarPlant]);

  const getBatteryStatusText = useMemo(() => {
    switch (solarPlant?.bst?.rawValue) {
      case 0:
        return "Idle";
      case 1:
        return "Charging";
      case 2:
        return "Discharging";
      default:
        return "Unknown";
    }
  }, [solarPlant]);

  useEffect(() => {
    if (solarPlant) {
      const sccnInstances = ensureArray<IInstance>(solarPlant?.Sccn?.instances);
      const pvv0Instances = ensureArray<IInstance>(solarPlant?.PVV0?.instances);
      const pvp0Instances = ensureArray<IInstance>(solarPlant?.PVP0?.instances);
      const pvv1Instances = ensureArray<IInstance>(solarPlant?.PVV1?.instances);
      const pvp1Instances = ensureArray<IInstance>(solarPlant?.PVP1?.instances);
      const pvv2Instances = solarPlant?.PVV2?.instances ?? {};
      const pvp2Instances = solarPlant?.PVP2?.instances ?? {};
      const pvv3Instances = solarPlant?.PVV3?.instances ?? {};
      const pvp3Instances = solarPlant?.PVP3?.instances ?? {};
      const pvvInstances = Object.entries(solarPlant?.PVV?.instances || {}) as [
        string,
        IInstance
      ][];
      const pvpInstances = Object.entries(solarPlant?.PVP?.instances || {}) as [
        string,
        IInstance
      ][];

      const pvChargerListOne = sccnInstances.map((sccnInstance, index) => {
        const pvv0Instance = pvv0Instances[index];
        const pvp0Instance = pvp0Instances[index];
        return {
          attributeCode: `${sccnInstance.formattedValue} - Solar 1`,
          value1: pvv0Instance?.formattedValue,
          value2: pvp0Instance?.formattedValue,
        };
      });
      const pvChargerListTwo = sccnInstances.map((sccnInstance, index) => {
        const pvv1Instance = pvv1Instances[index];
        const pvp1Instance = pvp1Instances[index];
        return {
          attributeCode: `${sccnInstance.formattedValue} - Solar 2`,
          value1: pvv1Instance?.formattedValue,
          value2: pvp1Instance?.formattedValue,
        };
      });
      const pvChargerListThree = sccnInstances.map((sccnInstance, index) => {
        const pvv2Instance = pvv2Instances[index];
        const pvp2Instance = pvp2Instances[index];
        return {
          attributeCode: `${sccnInstance.formattedValue} - Solar 3`,
          value1: pvv2Instance?.formattedValue,
          value2: pvp2Instance?.formattedValue,
        };
      });
      const pvChargerListFour = sccnInstances.map((sccnInstance, index) => {
        const pvv3Instance = pvv3Instances[index];
        const pvp3Instance = pvp3Instances[index];
        return {
          attributeCode: `${sccnInstance.formattedValue} - Solar 4`,
          value1: pvv3Instance?.formattedValue,
          value2: pvp3Instance?.formattedValue,
        };
      });
      const pvvChargerValues = pvvInstances.map(([key, pvvInstance]) => {
        const pvpInstance = pvpInstances.find(
          ([pvpKey]) => pvpKey === key
        )?.[1];

        return {
          attributeCode: `MPPT-${key}`,
          value1: pvvInstance.formattedValue,
          value2: pvpInstance?.formattedValue,
        };
      });

      const gridAttributes =
        solarPlant.from_to_grid.consistsOfAttributeCodes.map(
          (code: string) => ({
            attributeCode: `L${code.split("")[1]}`,
            value1: solarPlant?.[`IV${code.split("")[1]}`].formattedValue,
            value2: solarPlant?.[`II${code.split("")[1]}`].formattedValue,
            value3: solarPlant?.[code].formattedValue,
          })
        );
      const pvInverterAttributes =
        solarPlant.pv_inverter.consistsOfAttributeCodes.map((code: string) => ({
          attributeCode: `L${code.split("")[2]}`,
          value1: solarPlant?.[`pP${code.split("")[2]}`].formattedValue,
        }));
      const loadAttributes = solarPlant.ac_loads.consistsOfAttributeCodes.map(
        (code: string) => ({
          attributeCode: `L${code.split("")[1]}`,
          value1: solarPlant?.[`a${code.split("")[1]}`].formattedValue,
          value2: solarPlant?.OF.formattedValue,
        })
      );

      setGridAttributes(gridAttributes.filter((fl) => fl.value3));
      setPvInverterAttributes(pvInverterAttributes);
      setLoadAttributes(loadAttributes.filter((fl) => fl.value1));
      setDcPowerAttributes([
        { attributeCode: "Voltage", value1: solarPlant?.CV.formattedValue },
        {
          attributeCode: "Current",
          value1: solarPlant?.CI.formattedValue,
        },
      ]);
      setBatteryAttributes([
        {
          value1: solarPlant?.bv.formattedValue,
          value2: solarPlant?.bc.formattedValue,
          value3: solarPlant?.bp.formattedValue,
        },
      ]);
      setPvChargerAttributes([
        ...pvChargerListOne,
        ...pvChargerListTwo,
        ...pvChargerListThree,
        ...pvChargerListFour,
        ...pvvChargerValues,
      ]);
    }
  }, [solarPlant]);

  const getSourceValue = (value: string): number => {
    if (!value) return 0;

    return Number(value.split(" ")[0]);
  };

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState<{
    width: number;
    height: number;
  }>({
    width: 0,
    height: 0,
  });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resize = () => {
      const children = Array.from(container.children) as HTMLElement[];
      let maxWidth = 0;
      let maxHeight = 0;

      const containerRect = container.getBoundingClientRect();

      children.forEach((child) => {
        const rect = child.getBoundingClientRect();
        const right = rect.right - containerRect.left;
        const bottom = rect.bottom - containerRect.top;
        maxWidth = Math.max(maxWidth, right);
        maxHeight = Math.max(maxHeight, bottom);
      });

      setContainerSize({
        width: Math.ceil(maxWidth),
        height: Math.ceil(maxHeight),
      });
    };

    const observer = new ResizeObserver(resize);
    observer.observe(container);
    Array.from(container.children).forEach((child) =>
      observer.observe(child as HTMLElement)
    );

    resize(); // initial call

    return () => observer.disconnect();
  }, []);

  return (
    <div
      className={styles.vrmEnergyFlowContainer}
      ref={containerRef}
      style={{
        width: `${containerSize.width}px`,
        height: `${containerSize.height}px`,
      }}
    >
      <EnergyFlowNode
        x={320}
        y={0}
        title="Grid"
        value={getGridValue}
        icon="flash_on"
        iconColor="#3E6E8C"
        iconBgColor="#EEF6F8"
        siteEnergySource={
          <SiteEnergySource
            data={chartData}
            stopColor="#29B4CC14"
            stroke="#299BCC80"
            id="colorUv"
            hide={!getGridValue}
          />
        }
        otherInfo={
          <>
            {getGridValue &&
              gridAttributes &&
              gridAttributes.length > 0 &&
              gridAttributes.map((attr) => {
                return (
                  <Row className="row-cols-auto justify-content-between px-3 py-2 pb-0 text-light font-weight-400 font-size-12">
                    <Col>{attr.attributeCode}</Col>
                    <Col>{attr.value1}</Col>
                    <Col>{attr.value2}</Col>
                    <Col>{attr.value3}</Col>
                  </Row>
                );
              })}
          </>
        }
      />
      <EnergyFlowNode
        x={0}
        y={0}
        title="Generator"
        value={getGeneratorValue}
        icon="charger"
        iconColor="#A45796"
        iconBgColor="#F4EEF8"
        siteEnergySource={
          <SiteEnergySource
            data={chartData}
            stopColor="#A4579614"
            stroke="#A4579680"
            id="colorUx"
            hide={!getGeneratorValue}
          />
        }
      />
      <EnergyFlowNode
        x={0}
        y={290}
        title="DC Power"
        value={getDCPowerValue}
        icon="battery_charging_80"
        iconColor="#5E45B8"
        iconBgColor="#EFEEF8"
        siteEnergySource={
          <SiteEnergySource
            data={chartData}
            stopColor="#4C29CC14"
            stroke="#4C29CC59"
            id="colorUz"
            hide={!getDCPowerValue}
          />
        }
        otherInfo={
          <>
            {getDCPowerValue &&
              dcPowerAttributes &&
              dcPowerAttributes.length > 0 &&
              dcPowerAttributes.map((attr) => {
                return (
                  <Row className="row-cols-auto justify-content-between px-3 py-2 pb-0 text-light font-weight-400 font-size-12">
                    <Col>{attr.attributeCode}</Col>
                    <Col>{attr.value1}</Col>
                  </Row>
                );
              })}
          </>
        }
      />
      <EnergyFlowNode
        x={0}
        y={530}
        title={getBatteryStatusText}
        value={getBatteryValue}
        value2={getBatteryPowerValue}
        icon="battery_charging_80"
        iconColor="#5E45B8"
        iconBgColor="#EFEEF8"
        siteEnergySource={
          <SiteEnergySource
            data={chartData}
            stopColor="#4C29CC14"
            stroke="#4C29CC59"
            id="colorUq"
            hide={!getBatteryValue}
          />
        }
        otherInfo={
          <>
            {getBatteryPowerValue &&
              batteryAttributes &&
              batteryAttributes.length > 0 &&
              batteryAttributes.map((attr) => {
                return (
                  <Row className="row-cols-auto justify-content-between px-3 py-2 pb-0 text-light font-weight-400 font-size-12">
                    <Col>{attr.value1}</Col>
                    <Col>{attr.value2}</Col>
                    <Col>{attr.value3}</Col>
                  </Row>
                );
              })}
          </>
        }
      />
      <EnergyFlowNode
        x={320}
        y={530}
        title="PV Charger"
        value={`${getPVChargerValue} W`}
        icon="light_mode"
        iconColor="#D08700"
        iconBgColor="#F9F0E0"
        siteEnergySource={
          <SiteEnergySource
            data={chartData}
            stopColor="#D0870014"
            stroke="#D0870080"
            id="colorUc"
          />
        }
        otherInfo={
          <>
            {getPVChargerValue &&
            pvChargerAttributes &&
            pvChargerAttributes.length > 0 ? (
              pvChargerAttributes.map((attr) => {
                return attr.value1 ? (
                  <Row
                    className={`${
                      !attr.attributeCode?.includes("Solar")
                        ? `flex-row`
                        : `flex-column`
                    }  px-3 py-2 pb-0 text-light font-weight-400 font-size-12`}
                  >
                    <Col>{attr.attributeCode}</Col>
                    <Col>
                      <Row>
                        <Col>{attr.value1}</Col>
                        <Col>{attr.value2}</Col>
                      </Row>
                    </Col>
                  </Row>
                ) : (
                  <></>
                );
              })
            ) : (
              <></>
            )}
          </>
        }
      />
      <EnergyFlowNode
        x={640}
        y={530}
        title="PV Inverter"
        value={getPVInverterValue}
        icon="light_mode"
        iconColor="#D08700"
        iconBgColor="#F9F0E0"
        siteEnergySource={
          <SiteEnergySource
            data={chartData}
            stopColor="#D0870014"
            stroke="#D0870080"
            id="colorUw"
            hide={!getPVInverterValue}
          />
        }
        otherInfo={
          <>
            {getPVInverterValue &&
              pvInverterAttributes &&
              pvInverterAttributes.length > 0 &&
              pvInverterAttributes.map((attr) => {
                return (
                  <Row className="row-cols-auto justify-content-between px-3 py-2 pb-0 text-light font-weight-400 font-size-12">
                    <Col>{attr.attributeCode}</Col>
                    <Col>{attr.value1}</Col>
                  </Row>
                );
              })}
          </>
        }
      />
      <EnergyFlowNode
        x={640}
        y={290}
        title="Load"
        value={getLoadValue}
        icon="smart_outlet"
        iconColor="#E45151"
        iconBgColor="#FAF4F4"
        siteEnergySource={
          <SiteEnergySource
            data={chartData}
            stopColor="#E4515114"
            stroke="#E4515180"
            id="colorUr"
            hide={!getLoadValue}
          />
        }
        otherInfo={
          <>
            {getLoadValue &&
              loadAttributes &&
              loadAttributes.length > 0 &&
              loadAttributes.map((attr) => {
                return (
                  <Row className="row-cols-auto justify-content-between px-3 py-2 pb-0 text-light font-weight-400 font-size-12">
                    <Col>{attr.attributeCode}</Col>
                    <Col>{attr.value1}</Col>
                    <Col>{attr.value2}</Col>
                  </Row>
                );
              })}
          </>
        }
      />
      <EnergyFlowPath //Grid path
        x={400}
        y={90}
        direction={getSourceValue(getGridValue) > 0 ? "down" : "up"}
        isActive={getSourceValue(getGridValue) !== 0}
        d={`m 55 0 l 0 159`}
      />
      <EnergyFlowPath //Generator path
        x={78}
        y={70}
        direction={getSourceValue(getGeneratorValue) > 0 ? "up" : "down"}
        isActive={getSourceValue(getGeneratorValue) !== 0}
        d={`m 57 0 l 0 180 l 318 0`}
      />
      <EnergyFlowPath //Grid and Generator Common path
        x={400}
        y={252}
        direction={
          getSourceValue(getGridValue) > 0 ||
          getSourceValue(getGeneratorValue) > 0
            ? "down"
            : "up"
        }
        isActive={
          getSourceValue(getGridValue) !== 0 ||
          getSourceValue(getGeneratorValue) !== 0
        }
        d={`m 55 0 l 0 80`}
      />
      <EnergyFlowPath //DC Power path
        x={-12}
        y={380}
        direction={getSourceValue(getDCPowerValue) > 0 ? "up" : "down"}
        isActive={getSourceValue(getDCPowerValue) !== 0}
        d={`m 45 0 l 0 180`}
      />
      <EnergyFlowPath //Battery path
        x={78}
        y={550}
        direction={getBatteryStatus === 1 ? "up" : "down"}
        isActive={!!getBatteryStatus && getBatteryStatus !== 0}
        d={`m 57 0 l 0 -60 l 320 0`}
      />
      <EnergyFlowPath //PV Charger path
        x={400}
        y={500}
        direction={getPVChargerValue < 0 ? "down" : "up"}
        isActive={getPVChargerValue !== 0}
        d={`m 55 -9 l 0 60`}
      />
      <EnergyFlowPath //PV Charger and Battery Common path
        x={400}
        y={398}
        direction={
          getPVChargerValue < 0 ||
          (!getPVChargerValue && getBatteryStatus === 1)
            ? "down"
            : "up"
        }
        isActive={
          getPVChargerValue !== 0 ||
          (!!getBatteryStatus && getBatteryStatus !== 0)
        }
        d={`m 55 0 l 0 90`}
      />
      <EnergyFlowPath //PV Inverter path
        x={580}
        y={385}
        direction={getSourceValue(getPVInverterValue) > 0 ? "up" : "down"}
        isActive={getSourceValue(getPVInverterValue) !== 0}
        d={`m 15 0 l 0 105 l 180 0 m 0 0 l 0 60`}
      />
      <EnergyFlowPath //Load path
        x={510}
        y={385}
        direction={getSourceValue(getLoadValue) > 0 ? "down" : "up"}
        isActive={getSourceValue(getLoadValue) !== 0}
        d={`m 85 0 l 0 0 l 80 0`}
      />
      <EnergyFlowPath //Load and Inverter common path
        x={400}
        y={385}
        direction={
          getSourceValue(getLoadValue) > 0 ||
          (!getLoadValue && getSourceValue(getPVInverterValue) < 0)
            ? "down"
            : "up"
        }
        isActive={
          getSourceValue(getLoadValue) !== 0 ||
          getSourceValue(getPVInverterValue) !== 0
        }
        d={`m 85 0 l 0 0 l 110 0`}
      />
      <div
        className="position-absolute"
        style={{ left: 416 + "px", top: 300 + "px" }}
      >
        <Row className={`${styles.energyFlowCenterTop}`}>
          <Col className="text-center">
            <img src={EnergyFlowImg} alt="Energy Flow" />
          </Col>
        </Row>
        <Row className={`${styles.energyFlowCenterBottom}`}>
          <Col className="text-center text-primary font-weight-400 font-size-12">
            {solarPlant?.ss?.formattedValue}
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default VrmEnergyFlow;
